export function formatBpmJobResult(data) {
  const processed = data.import_total ?? data.total ?? 0;
  const updated = data.items_updated;
  if (!processed && !updated) return "";
  const parts = [];
  if (processed)
    parts.push(`${processed} track${processed === 1 ? "" : "s"} processed`);
  if (updated !== undefined)
    parts.push(`${updated} track${updated === 1 ? "" : "s"} updated`);
  return parts.join(", ");
}

export function formatAdminMessage(data) {
  if (data && typeof data === "object" && data.import_total !== undefined) {
    const processed = data.import_total ?? 0;
    const parts = [`${processed} item${processed === 1 ? "" : "s"} processed`];
    if (data.items_updated !== undefined) {
      parts.push(
        `${data.items_updated} item${data.items_updated === 1 ? "" : "s"} updated`,
      );
    }
    return parts.join(", ");
  }
  if (data && typeof data === "object") {
    const total = data.total ?? data.count;
    if (typeof total === "number") {
      return `${total} item${total === 1 ? "" : "s"}`;
    }
  }
  return (data && (data.detail || data.message)) || "Completed";
}

export function formatAdminResult(data, op) {
  if (typeof data === "object" && data !== null) {
    if (typeof op.formatResult === "function") {
      return op.formatResult(data);
    }
    return { message: formatAdminMessage(data) };
  }
  if (typeof data === "string") {
    return { message: data };
  }
  return { message: "Completed" };
}

function resolveContextId(op, context) {
  if (!context) return null;
  if (op.needsAlbum) return context.albumId;
  if (op.needsArtist) return context.artistId;
  if (op.needsTrack) return context.trackId;
  if (op.needsId) return context.input;
  return null;
}

export function buildAdminEndpoint(op, context) {
  const requiresId =
    op.needsAlbum || op.needsArtist || op.needsTrack || op.needsId;
  const id = resolveContextId(op, context);
  if (requiresId && !id) {
    return { endpoint: null, missingId: true };
  }
  let endpoint =
    typeof op.endpoint === "function" ? op.endpoint(id) : op.endpoint;
  const userIds = Array.isArray(context?.userIds) ? context.userIds : [];
  if (op.needsUser && userIds.length) {
    const qs = userIds
      .map((uid) => `discogs_id=${encodeURIComponent(uid)}`)
      .join("&");
    endpoint += endpoint.includes("?") ? `&${qs}` : `?${qs}`;
  }
  return { endpoint, missingId: false };
}

export function buildAdminRequestOptions(op, context) {
  let headers =
    typeof op.headers === "function"
      ? op.headers(context)
      : op.headers || undefined;
  let body;
  if (op.jsonBody !== undefined) {
    const payload =
      typeof op.jsonBody === "function" ? op.jsonBody(context) : op.jsonBody;
    if (payload !== undefined) {
      body = JSON.stringify(payload);
      headers = { ...(headers || {}), "Content-Type": "application/json" };
    }
  } else if (op.body !== undefined) {
    body = typeof op.body === "function" ? op.body(context) : op.body;
  }
  return { headers, body };
}

export function interpretRunResult(response, status, op) {
  const data = response || {};
  if (
    ["started", "running", "stopping"].includes(data.status) ||
    (status === 202 &&
      !["completed", "stopped", "failed", "none"].includes(data.status))
  ) {
    const runningStatus =
      typeof data === "object" && data !== null
        ? data
        : { message: "Running..." };
    return {
      shouldPoll: true,
      status: runningStatus,
      resetInputs: false,
    };
  }
  const result = resolveAdminResult(data, op);
  return {
    shouldPoll: false,
    status: result.status,
    resetInputs: result.resetInputs,
  };
}

export function resolveAdminResult(data, op) {
  if (data && typeof data === "object") {
    if (["started", "running", "stopping"].includes(data.status)) {
      return { kind: "running", status: data, resetInputs: false };
    }
    if (data.status === "stopped" || data.status === "none") {
      return {
        kind: "completed",
        status: {
          message: data.status === "stopped" ? "Job stopped" : "No job",
        },
        resetInputs: false,
      };
    }
    if (data.status === "failed") {
      const message = data.detail || data.message || "Operation failed";
      return {
        kind: "error",
        status: { error: message },
        resetInputs: false,
      };
    }
    if (data.status === "completed") {
      const formatted = formatAdminResult(data, op);
      return {
        kind: "completed",
        status:
          typeof formatted === "string" ? { message: formatted } : formatted,
        resetInputs: true,
      };
    }
  }
  const formatted = formatAdminResult(data, op);
  return {
    kind: "completed",
    status: typeof formatted === "string" ? { message: formatted } : formatted,
    resetInputs: true,
  };
}
